import React, { useState } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import API_URL from '../api/baseURL';
import SearchableSelect from '../components/SearchableSelect';


const VehicleReceptionForm = () => {
  const { token, user } = useAuth();

  const [form, setForm] = useState({
    vehicleId: '',
    reasonOfVisit: '',
    fuelLevel: 0,
    glassBroken: false,
    scratchesOrDents: '',
    sideMirrorDamage: false,
    headlightDamage: false,
    tireBoltIssue: false,
    tireCondition: 'Good',
    odometer: '',
    dashboardWarningLights: '',
    customerRemarks: '',
    technicianComments: '',
    exteriorCondition: 'Good',
    interiorCondition: 'Normal',
    numberOfKeys: 1,
    personalItemsLeft: ''
  });

  const { data: vehicles = [], isLoading } = useQuery({
    queryKey: ['vehicles'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/vehicles`);
      return response.data;
    }
  });

  const [message, setMessage] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/checklist`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage('✅ Checklist submitted successfully!');
      setForm({
        vehicleId: '',
        reasonOfVisit: '',
        fuelLevel: 0,
        glassBroken: false,
        scratchesOrDents: '',
        sideMirrorDamage: false,
        headlightDamage: false,
        tireBoltIssue: false,
        tireCondition: 'Good',
        odometer: '',
        dashboardWarningLights: '',
        customerRemarks: '',
        technicianComments: '',
        exteriorCondition: 'Good',
        interiorCondition: 'Normal',
        numberOfKeys: 1,
        personalItemsLeft: ''
      });
    } catch (err) {
      console.error('❌ Submission error:', err);
      setMessage('❌ Failed to submit checklist.');
    }
  };

  return (
    <div className="max-w-xl mx-auto p-6 bg-white shadow rounded mt-6">
      <h2 className="text-xl font-bold mb-4">📋 Vehicle Reception Checklist</h2>
      {message && <p className="mb-4">{message}</p>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block font-semibold">Vehicle</label>
          <SearchableSelect
            options={vehicles.map(v => ({ value: v._id, label: `${v.plateNumber} - ${v.model}` }))}
            value={form.vehicleId}
            onChange={(val) => handleChange({ target: { name: 'vehicleId', value: val } })}
            placeholder="Select vehicle"
            name="vehicleId"
            required
          />
        </div>

        <div>
          <label className="block font-semibold">Reason of Visit</label>
          <textarea name="reasonOfVisit" value={form.reasonOfVisit} onChange={handleChange} className="border p-2 rounded w-full" placeholder="e.g. Oil change, engine noise, brake inspection..." rows={2} />
        </div>

        <div>
          <label className="block font-semibold">Fuel Level (%)</label>
          <input type="number" name="fuelLevel" value={form.fuelLevel} onChange={handleChange} className="border p-2 rounded w-full" min="0" max="100" required />
        </div>

        <div>
          <label className="block font-semibold">Odometer (km)</label>
          <input type="number" name="odometer" value={form.odometer} onChange={handleChange} className="border p-2 rounded w-full" />
        </div>

        {/* ✅ NEW: General Condition Fields */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block font-semibold">Exterior Condition</label>
            <select name="exteriorCondition" value={form.exteriorCondition} onChange={handleChange} className="border p-2 rounded w-full">
              <option value="Excellent">Excellent</option>
              <option value="Good">Good</option>
              <option value="Fair">Fair</option>
              <option value="Poor">Poor</option>
            </select>
          </div>
          <div>
            <label className="block font-semibold">Interior Condition</label>
            <select name="interiorCondition" value={form.interiorCondition} onChange={handleChange} className="border p-2 rounded w-full">
              <option value="Clean">Clean</option>
              <option value="Normal">Normal</option>
              <option value="Dirty">Dirty</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block font-semibold">Number of Keys Received</label>
          <input type="number" name="numberOfKeys" value={form.numberOfKeys} onChange={handleChange} className="border p-2 rounded w-full" min="0" max="5" />
        </div>

        <div>
          <label className="block font-semibold">Personal Items Left in Vehicle</label>
          <input type="text" name="personalItemsLeft" value={form.personalItemsLeft} onChange={handleChange} className="border p-2 rounded w-full" placeholder="e.g. Phone charger, sunglasses" />
        </div>

        <div>
          <label className="block font-semibold">Tire Condition</label>
          <select name="tireCondition" value={form.tireCondition} onChange={handleChange} className="border p-2 rounded w-full">
            <option value="Good">Good</option>
            <option value="Worn">Worn</option>
            <option value="Damaged">Damaged</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-center">
            <input type="checkbox" name="glassBroken" checked={form.glassBroken} onChange={handleChange} className="mr-2" />
            <label>Glass Broken</label>
          </div>
          <div className="flex items-center">
            <input type="checkbox" name="sideMirrorDamage" checked={form.sideMirrorDamage} onChange={handleChange} className="mr-2" />
            <label>Side Mirror Damage</label>
          </div>
          <div className="flex items-center">
            <input type="checkbox" name="headlightDamage" checked={form.headlightDamage} onChange={handleChange} className="mr-2" />
            <label>Headlight Damage</label>
          </div>
          <div className="flex items-center">
            <input type="checkbox" name="tireBoltIssue" checked={form.tireBoltIssue} onChange={handleChange} className="mr-2" />
            <label>Tire Bolt Issue</label>
          </div>
        </div>

        <div>
          <label className="block font-semibold">Scratches or Dents</label>
          <textarea name="scratchesOrDents" value={form.scratchesOrDents} onChange={handleChange} className="border p-2 rounded w-full" placeholder="Describe any visible damage..." />
        </div>

        <div>
          <label className="block font-semibold">Dashboard Warning Lights</label>
          <textarea name="dashboardWarningLights" value={form.dashboardWarningLights} onChange={handleChange} className="border p-2 rounded w-full" />
        </div>

        <div>
          <label className="block font-semibold">Customer Remarks</label>
          <textarea name="customerRemarks" value={form.customerRemarks} onChange={handleChange} className="border p-2 rounded w-full" />
        </div>

        <div>
          <label className="block font-semibold">Technician Comments</label>
          <textarea name="technicianComments" value={form.technicianComments} onChange={handleChange} className="border p-2 rounded w-full" />
        </div>

        <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 w-full">
          Submit Checklist
        </button>
      </form>
    </div>
  );
};

export default VehicleReceptionForm;

